/**
 * To manage states using Redux instead of Zustand,
   uncomment commented codes "Redux codes",
   then comment Zustand codes
 */

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ErrorMessage, Field, Form, Formik } from "formik";
import diskIcon from "../../../../assets/images/diskIcon.svg";
import Modal from "../../../../components/Modal";
import * as Yup from "yup";
import {
  getAgencyById,
  getAgencyUsersList,
  putAgencyEdit,
} from "../../../../utils/api";
import { useAuth } from "../../../../hooks/useAuth";
import AsyncSelect from "react-select/async";
import { useDispatch, useSelector } from "react-redux";
import { fetchAgencyById } from "../../../../features/editAgencySlice";
import useStore from "../../../../store";

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Name is required!"),
  main_admin_id: Yup.object().required("Main Admin is required!"),
});

const CustomInputComponent = ({ field, form, label, loadOptions }) => {
  const [defaultOptions, setDefaultOptions] = useState([]);

  useEffect(() => {
    const fetchInitialOptions = async () => {
      const options = await loadOptions("", true);
      setDefaultOptions(options);
    };

    fetchInitialOptions();
  }, [loadOptions]);

  const handleChange = (selectedOption) => {
    form.setFieldValue(field.name, selectedOption ? selectedOption : "");
  };

  return (
    <div>
      <label className="flex text-[12px] font-[600] text-[#001522]">
        {label}
      </label>
      <AsyncSelect
        name={field.name}
        loadOptions={(inputValue) => loadOptions(inputValue)}
        onChange={handleChange}
        onBlur={field.onBlur}
        isSearchable
        defaultOptions={defaultOptions}
        cacheOptions
        value={form.values[field.name]}
      />
    </div>
  );
};

const EditAgencyForm = () => {
  const params = useParams();
  const dispatch = useDispatch();
  const { agencyData, loading, error } = useSelector((state) => state.editAgency);
  // const { setAgency, agency } = useStore();
  // const { agencyData, loading, error } = agency;
  const { accessToken } = useAuth();
  const [modalMessage, setModalMessage] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!accessToken) return;

    dispatch(fetchAgencyById({ accessToken, id: params.id }))

    // const fetchAgencyById_Zustand = async (token, id) => {
    //   const response = await getAgencyById(token, id);
    //   const error = !response.data.success;
    //   if (error) {
    //     setAgency({ ...agency, loading: false, error });
    //     throw new Error("Failed to fetch agency");
    //   }
    //   return response.data;
    // };
    // const data = fetchAgencyById_Zustand(accessToken, params.id);
    // setAgency({ data, loading: false, error: null });
  }, [
    accessToken,
    dispatch,
    // setAgency,
    params.id,
  ]);

  const fetchUsers = async (inputValue) => {
    if (!accessToken) return [];

    try {
      const response = await getAgencyUsersList(accessToken, inputValue);
      if (response.data.success) {
        return response.data.data.map((user) => ({
          label: user.name,
          value: user.id,
        }));
      }
    } catch (error) {
      setIsError(true);
      setModalMessage(error.response.data.message);
      setIsModalOpen(true);
    }
    return [];
  };

  const loadOptions = async (inputValue = "", isInitial = false) => {
    if (isInitial || inputValue.length >= 2) {
      return await fetchUsers(inputValue);
    }
    return [];
  };

  const handleSubmit = (values, { setSubmitting }) => {
    putAgencyEdit(accessToken, values, params.id)
      .then((res) => {
        if (res.data.success) {
          setIsError(false);
          setModalMessage("Agency was successfully updated!");
          setIsModalOpen(true);
        }
      })
      .catch((err) => {
        setIsError(true);
        setModalMessage(err.response.data.message);
        setIsModalOpen(true);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  return (
    <Formik
      initialValues={{
        ...agencyData,
        main_admin_id: agencyData.main_admin
          ? {
              label: agencyData.main_admin.user,
              value: agencyData.main_admin.id,
            }
          : null,
      }}
      validationSchema={validationSchema}
      enableReinitialize={true}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit, isSubmitting }) => {
        return (
          <>
            {loading ? (
              <p>Loading...</p>
            ) : error ? (
              <p>Error: {error}</p>
            ) : (
              <Form
                className="bg-[#F7F7F7] createAgencyForm"
                onSubmit={handleSubmit}
              >
                <div className="p-8 pt-0">
                  <div className="flex flex-col lg:flex-row">
                    <div className="w-full">
                      <div className="bg-white rounded-[10px] p-[25px] border border-[#E0E0E0] lg:mr-[35px] gap-[22px]">
                        <div className="mb-[22px]">
                          <label
                            htmlFor="name"
                            className="flex text-[12px] font-[600] text-[#001522]"
                          >
                            Name
                          </label>
                          <Field
                            type="text"
                            id="name"
                            name="name"
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md focus-visible:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                          />
                          <ErrorMessage
                            name="name"
                            component="div"
                            className="text-red-500 text-xs"
                          />
                        </div>
                        <div className="mb-[22px]">
                          <label
                            htmlFor="main_admin_id"
                            className="flex text-[12px] font-[600] text-[#001522]"
                          >
                            Main Admin
                          </label>
                          <Field
                            name="main_admin_id"
                            component={CustomInputComponent}
                            loadOptions={loadOptions}
                          />
                          <ErrorMessage
                            name="main_admin_id"
                            component="div"
                            className="text-red-500 text-xs"
                          />
                        </div>
                        <div className="mb-[22px]">
                          <label
                            htmlFor="group"
                            className="flex pb-[10px] text-[12px] font-[600] text-[#001522]"
                          >
                            Status
                          </label>
                          <div
                            role="group"
                            aria-labelledby="my-radio-group"
                            className="flex flex-col gap-[10px]"
                          >
                            <label className="flex items-center">
                              <Field
                                type="radio"
                                name="status"
                                value="active"
                                className="mr-[3px]"
                              />
                              <span className="text-[12px] pl-[5px] pt-[2px]">
                                Active
                              </span>
                            </label>
                            <label className="flex items-center">
                              <Field
                                type="radio"
                                name="status"
                                value="not_active"
                              />
                              <span className="text-[12px] pl-[5px] pt-[2px]">
                                Not Active
                              </span>
                            </label>
                            <label className="flex items-center">
                              <Field
                                type="radio"
                                name="status"
                                value="deleted"
                              />
                              <span className="text-[12px] pl-[5px] pt-[2px]">
                                Deleted
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="sticky w-full bottom-0 left-0 flex justify-between bg-white border-t border-t-[#E0E0E0] py-[18px] px-[35px]">
                  <div className="flex">
                    <button
                      type="submit"
                      className={`flex bg-gradient text-white text-[10px] font-[600] ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""} rounded-[5px] px-[13px] py-[8px] mr-[13px]`}
                      disabled={isSubmitting}
                    >
                      <img
                        src={diskIcon}
                        alt="Disk Icon"
                        className="mr-[5px]"
                      />
                      Save
                    </button>
                  </div>

                  {isModalOpen && (
                    <Modal
                      message={modalMessage}
                      isError={isError}
                      onClose={() => setIsModalOpen(false)}
                    />
                  )}
                </div>
              </Form>
            )}
          </>
        );
      }}
    </Formik>
  );
};

export default EditAgencyForm;
